/**
 * App de MARCA (white label do Meu Contador, 2026-10-20).
 *
 * A pessoa entra por /w/<slug> (ou pelo domínio da marca): a identidade
 * publicada fica guardada no aparelho e o app inteiro passa a vestir a marca
 * (nome, logo, cores, ícone do PWA). O login manda `marca` junto, e o servidor
 * só abre empresa ligada a ela. Sem marca guardada, o Meu Contador de sempre.
 */
import axios from 'axios';
import { useEffect, useState } from 'react';

const CHAVE = 'mc_wl_marca';
const EVENTO = 'mc-wl-marca';

export function lerMarca() {
  try {
    const m = JSON.parse(localStorage.getItem(CHAVE) || 'null');
    return m && m.slug && m.tema ? m : null;
  } catch (_) { return null; }
}
export function salvarMarca(m) {
  try { localStorage.setItem(CHAVE, JSON.stringify(m)); } catch (_) { /* sem armazenamento: vale só nesta aba */ }
  _atual = m;
  window.dispatchEvent(new Event(EVENTO));
}
export function limparMarca() {
  try { localStorage.removeItem(CHAVE); } catch (_) { /* nada */ }
  _atual = null;
  document.cookie = 'wl_marca=; Path=/; Max-Age=0';
  window.dispatchEvent(new Event(EVENTO));
}

let _atual = typeof window !== 'undefined' ? lerMarca() : null;

/**
 * Domínio próprio da marca: o servidor põe <meta name="wl-marca"> no HTML. Quem chega
 * direto em app.marcadele.com.br/cliente/login (sem passar por /w/<slug>) já é da marca.
 */
export function slugDoHtml() {
  try {
    const el = typeof document !== 'undefined' && document.querySelector('meta[name="wl-marca"]');
    const v = el ? String(el.getAttribute('content') || '').trim() : '';
    return /^[a-z0-9-]{3,40}$/.test(v) ? v : null;
  } catch (_) { return null; }
}

/** Busca a identidade publicada. null = marca fora do ar. */
export async function buscarMarca(slug) {
  try {
    const { data } = await axios.get(`/api/wl/${encodeURIComponent(slug)}`);
    return data && data.marca ? data.marca : null;
  } catch (e) {
    if (e.response && e.response.status === 404) return null;
    throw e;
  }
}

const _conferidasNestaAbertura = new Set();

/** Marca ativa + atualização em segundo plano (pega logo/cores novas). */
export function useMarca() {
  const [marca, setMarca] = useState(() => _atual || lerMarca());
  useEffect(() => {
    const ao = () => setMarca(lerMarca());
    window.addEventListener(EVENTO, ao);
    return () => window.removeEventListener(EVENTO, ao);
  }, []);
  useEffect(() => {
    // domínio da marca sem marca guardada (primeira visita direto no endereço dela)
    const slugHtml = slugDoHtml();
    if (marca || !slugHtml || typeof window === 'undefined' || !window.location.pathname.startsWith('/cliente')) return;
    let vivo = true;
    buscarMarca(slugHtml).then((m) => { if (vivo && m) salvarMarca({ ...m, _conferida: true }); }).catch(() => {});
    return () => { vivo = false; };
  }, [marca]);
  useEffect(() => {
    // Confere uma vez por abertura do app (o `_conferida` guardado no aparelho fazia a
    // marca nunca mais atualizar: logo, cores e planos novos não chegavam).
    if (!marca || _conferidasNestaAbertura.has(marca.slug)) return;
    _conferidasNestaAbertura.add(marca.slug);
    let vivo = true;
    buscarMarca(marca.slug).then((m) => {
      if (!vivo) return;
      if (!m) { limparMarca(); return; }
      salvarMarca({ ...m, _conferida: true });
    }).catch(() => { /* sem rede: segue com a guardada */ });
    return () => { vivo = false; };
  }, [marca]);
  return marca;
}

// O app de marca leva a marca: no LOGIN (o servidor só abre empresa ligada a ela) e,
// no cabeçalho `x-wl-marca`, em todo pedido do cliente (sessão de outra empresa cai).
if (typeof window !== 'undefined' && !window.__mcWlInterceptor) {
  window.__mcWlInterceptor = true;
  axios.interceptors.request.use((config) => {
    const guardada = _atual || lerMarca();
    const pathAtual = typeof window !== 'undefined' ? window.location.pathname : '';
    const slugHtml = pathAtual.startsWith('/cliente') ? slugDoHtml() : null;
    const m = guardada || (slugHtml ? { slug: slugHtml } : null);
    const url = String(config.url || '');
    if (m && url.includes('/cliente/')) {
      config.headers = config.headers || {};
      config.headers['x-wl-marca'] = m.slug;
      if (/\/cliente\/login/.test(url) && String(config.method || '').toLowerCase() === 'post') {
        const corpo = typeof config.data === 'string' ? (() => { try { return JSON.parse(config.data); } catch (_) { return null; } })() : config.data;
        if (corpo && typeof corpo === 'object' && !corpo.marca) config.data = { ...corpo, marca: m.slug };
      }
    }
    return config;
  });
}

/** Nomes que aparecem nos textos do app: os da marca ou os de sempre. PURA. */
export function nomesDoApp(marca) {
  return marca
    ? { app: marca.nome_app, casa: marca.nome_app, escritorio: marca.nome_app }
    : { app: 'Meu Contador', casa: 'Marçal', escritorio: 'Marçal Contabilidade' };
}

// ─── Cores ───────────────────────────────────────────────────────────────────
// O desenho é o do Meu Contador: a cor PRINCIPAL faz o papel do azul-marinho
// (cabeçalho, botões) e a de DESTAQUE o do azul claro (links, números, gráfico).
// O claro e o escuro seguem o interruptor do app (data-tema no .sa-root).

function rgb(hex) { const h = String(hex || '#000000').replace('#', ''); return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) || 0); }
function paraHex([r, g, b]) { return `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`; }
export function misturar(a, b, t) { const x = rgb(a); const y = rgb(b); return paraHex(x.map((v, i) => v + (y[i] - v) * t)); }
function alfa(hex, a) { const [r, g, b] = rgb(hex); return `rgba(${r},${g},${b},${a})`; }
function lum(hex) {
  return rgb(hex).map((v) => v / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4))
    .reduce((s, v, i) => s + v * [0.2126, 0.7152, 0.0722][i], 0);
}
export function contraste(a, b) { const [x, y] = [lum(a), lum(b)].sort((m, n) => n - m); return (x + 0.05) / (y + 0.05); }
/** Mexe na cor (rumo ao preto ou ao branco) até ter contraste com o fundo. PURA. */
function ajustar(cor, fundo, alvo, rumo) {
  let c = cor;
  for (let t = 0.05; t <= 1.0001 && contraste(c, fundo) < alvo; t += 0.05) c = misturar(cor, rumo, t);
  return c;
}

/** Os dois jogos de variáveis (claro e escuro) da marca. PURA. */
export function tokensDaMarca(tema) {
  if (!tema) return null;
  const P = tema.primaria;                       // já escura o bastante pra texto branco (servidor)
  const A = tema.destaque || misturar(P, '#ffffff', 0.45);
  // ── claro (papel branco, cabeçalho na cor principal) ──
  const tinta = misturar(P, '#0b0f17', 0.35);
  const acentoClaro = ajustar(A, '#ffffff', 3.2, '#000000');
  const claro = {
    '--sa-c-azul': P, '--sa-c-topo': P, '--sa-c-tinta': tinta, '--sa-c-suave': misturar(tinta, '#ffffff', 0.42),
    '--sa-c-ice': acentoClaro, '--sa-c-iceDeep': acentoClaro, '--sa-c-blue-mid': acentoClaro, '--sa-c-blue-pale': misturar(A, '#ffffff', 0.55),
    '--sa-c-fiscal': acentoClaro, '--sa-c-fiscalFundo': misturar(acentoClaro, '#ffffff', 0.88), '--sa-c-chipIcone': misturar(acentoClaro, '#ffffff', 0.88),
    '--sa-c-papel': misturar(P, '#ffffff', 0.955), '--sa-c-branco': '#ffffff', '--sa-c-card-bg': '#ffffff', '--sa-c-card2': misturar(P, '#ffffff', 0.98),
    // Bordas que se veem no claro (antes 0.9 sobre papel 0.955: sumiam). Campo de formulário ≥ 3:1 contra o branco.
    '--sa-c-borda': misturar(P, '#ffffff', 0.8), '--sa-c-card-borda': misturar(P, '#ffffff', 0.82), '--sa-c-divisor': misturar(P, '#ffffff', 0.88),
    '--sa-c-borda-campo': ajustar(misturar(P, '#ffffff', 0.55), '#ffffff', 3, P),
    '--sa-c-neutroFundo': misturar(P, '#ffffff', 0.94), '--sa-c-tabbar-bg': '#ffffff', '--sa-c-tabbar-opaca': '#ffffff',
    '--sa-c-btn-bg': P, '--sa-c-btn-fg': '#ffffff', '--sa-c-btn-borda': misturar(P, '#ffffff', 0.82), '--sa-c-tintaSobreGelo': '#ffffff',
    '--sa-c-track': misturar(P, '#ffffff', 0.9), '--sa-c-track-marca': misturar(P, '#ffffff', 0.68), '--sa-c-spark-off': misturar(acentoClaro, '#ffffff', 0.75),
    '--sa-c-skel': misturar(P, '#ffffff', 0.93), '--sa-c-skel2': misturar(P, '#ffffff', 0.9), '--sa-c-ink3': misturar(P, '#ffffff', 0.5),
    '--sa-c-card-sombra': `0 1px 2px ${alfa(P, 0.05)}, 0 12px 24px -18px ${alfa(P, 0.4)}`,
    '--sa-c-card-sombra-flut': `0 1px 2px ${alfa(P, 0.06)}, 0 18px 34px -20px ${alfa(P, 0.45)}`,
    '--sa-c-folha-sheet': `0 -18px 40px ${alfa(P, 0.16)}`,
    '--sa-paper-2': misturar(P, '#ffffff', 0.93), '--sa-surface': '#ffffff', '--sa-surface-h': misturar(P, '#ffffff', 0.97),
    '--sa-blue': acentoClaro, '--sa-blue-deep': P, '--sa-ice': acentoClaro, '--sa-ice-deep': acentoClaro, '--sa-link': acentoClaro,
    '--sa-info': acentoClaro, '--sa-info-soft': misturar(acentoClaro, '#ffffff', 0.88),
    '--sa-azul-100': misturar(acentoClaro, '#ffffff', 0.9), '--sa-azul-200': misturar(acentoClaro, '#ffffff', 0.82),
    '--sa-c-acao-bg': '#ffffff', '--sa-c-acao-borda': misturar(P, '#ffffff', 0.78),
    '--sa-paper': misturar(P, '#ffffff', 0.955), '--sa-ink': tinta, '--sa-ink-soft': misturar(tinta, '#ffffff', 0.42),
    '--sa-cream': '#ffffff', // texto SOBRE a cor principal (título das páginas internas); tinta deixava ilegível
    '--sa-rule': misturar(P, '#ffffff', 0.8), '--wl-ativo': alfa(acentoClaro, 0.12), '--wl-fundo': misturar(P, '#ffffff', 0.955),
  };
  // ── escuro (fundo profundo da cor principal) ──
  const papel = misturar(P, '#05070c', 0.32);
  const card = misturar(P, '#05070c', 0.12);
  const acentoEscuro = ajustar(A, card, 4.5, '#ffffff');
  const escuro = {
    '--sa-c-azul': misturar(P, '#05070c', 0.48), '--sa-c-topo': misturar(P, '#05070c', 0.48), '--sa-c-tinta': '#ffffff', '--sa-c-suave': misturar(acentoEscuro, '#ffffff', 0.55),
    '--sa-c-ice': acentoEscuro, '--sa-c-iceDeep': A, '--sa-c-blue-mid': acentoEscuro, '--sa-c-blue-pale': misturar(acentoEscuro, '#ffffff', 0.4),
    '--sa-c-fiscal': acentoEscuro, '--sa-c-fiscalFundo': alfa(acentoEscuro, 0.18), '--sa-c-chipIcone': alfa(acentoEscuro, 0.18),
    '--sa-c-papel': papel, '--sa-c-branco': card, '--sa-c-card-bg': card, '--sa-c-card2': misturar(P, '#05070c', 0.2),
    '--sa-c-borda': 'rgba(255,255,255,0.14)', '--sa-c-card-borda': 'rgba(255,255,255,0.14)', '--sa-c-divisor': 'rgba(255,255,255,0.08)',
    '--sa-c-borda-campo': 'rgba(255,255,255,0.4)',
    '--sa-c-neutroFundo': 'rgba(255,255,255,0.08)', '--sa-c-tabbar-bg': card, '--sa-c-tabbar-opaca': card,
    '--sa-c-btn-bg': acentoEscuro, '--sa-c-btn-fg': papel, '--sa-c-btn-borda': alfa(acentoEscuro, 0.45), '--sa-c-tintaSobreGelo': papel,
    '--sa-c-track': 'rgba(255,255,255,0.14)', '--sa-c-track-marca': 'rgba(255,255,255,0.7)', '--sa-c-spark-off': alfa(acentoEscuro, 0.38),
    '--sa-c-skel': 'rgba(255,255,255,0.08)', '--sa-c-skel2': 'rgba(255,255,255,0.14)', '--sa-c-ink3': 'rgba(255,255,255,0.55)',
    '--sa-c-card-sombra': 'inset 0 1px 0 rgba(255,255,255,0.06), 0 16px 32px -22px rgba(0,0,0,0.8)',
    '--sa-c-card-sombra-flut': 'inset 0 1px 0 rgba(255,255,255,0.08), 0 24px 44px -24px rgba(0,0,0,0.9)',
    '--sa-c-folha-sheet': '0 -20px 46px rgba(0,0,0,0.6)',
    '--sa-paper': papel, '--sa-ink': '#ffffff', '--sa-ink-soft': misturar(acentoEscuro, '#ffffff', 0.55),
    '--sa-cream': '#ffffff', '--sa-rule': 'rgba(255,255,255,0.1)', '--wl-ativo': alfa(acentoEscuro, 0.16), '--wl-fundo': papel,
    '--sa-blue': acentoEscuro, '--sa-blue-deep': misturar(P, '#05070c', 0.48), '--sa-blue-neon': acentoEscuro, '--sa-ice': acentoEscuro,
    '--sa-ice-deep': A, '--sa-link': acentoEscuro, '--sa-info': acentoEscuro, '--sa-info-soft': alfa(acentoEscuro, 0.18),
    '--sa-azul-100': alfa(acentoEscuro, 0.1), '--sa-azul-200': alfa(acentoEscuro, 0.18),
    '--sa-paper-2': misturar(P, '#05070c', 0.38), '--sa-surface': card, '--sa-surface-h': misturar(P, '#05070c', 0.05),
    '--sa-c-acao-bg': card, '--sa-c-acao-borda': 'rgba(255,255,255,0.12)',
  };
  return { claro, escuro };
}

const _bloco = (sel, vars) => `${sel}{${Object.entries(vars).map(([k, v]) => `${k}:${v}`).join(';')}}`;
const _textoCss = (t) => String(t || '').replace(/["\\<>\n\r]/g, '').slice(0, 40);

/** CSS da marca pro app (claro e escuro seguem o interruptor). PURA. */
export function cssDaMarca(marca) {
  const t = marca && tokensDaMarca(marca.tema);
  if (!t) return '';
  const r = '.sa-root[data-wl]';
  return [
    _bloco(r, t.escuro),
    _bloco(`${r}[data-tema="escuro"]`, t.escuro),
    _bloco(`${r}[data-tema="claro"]`, t.claro),
    // coluna da versão web: o nome da marca no lugar de "Marçal", item ativo na cor da marca
    `@media (min-width:1024px){${r} .sa-tabbar::before{content:"${_textoCss(marca.nome_app)}"}}`,
    `${r} .sa-tabbar-item:hover{background:var(--wl-ativo)}`,
    `${r} .sa-tabbar-item.is-active{background:var(--wl-ativo)}`,
  ].join('\n');
}

/** Aplica no <html> (login e telas fora do layout): o jogo escuro, como o login do app. */
export function aplicarNoDocumento(marca) {
  const el = document.documentElement;
  const t = marca ? tokensDaMarca(marca.tema) : null;
  // O login pinta o fundo com --sa-blue → --sa-blue-deep: aqui é a cor principal (não o destaque).
  const vars = t ? { ...t.escuro, '--sa-blue': marca.tema.primaria, '--sa-blue-deep': misturar(marca.tema.primaria, '#05070c', 0.6) } : {};
  const antigas = (el.dataset.wlVars || '').split(',').filter(Boolean);
  for (const k of antigas) if (!(k in vars)) el.style.removeProperty(k);
  for (const [k, val] of Object.entries(vars)) el.style.setProperty(k, val);
  el.dataset.wlVars = Object.keys(vars).join(',');
  if (marca) {
    el.setAttribute('data-wl', marca.slug);
    document.title = marca.nome_app;
    const tc = document.querySelector('meta[name="theme-color"]');
    if (tc) tc.setAttribute('content', marca.tema.primaria);
  } else el.removeAttribute('data-wl');
  return vars;
}

/** O tema a partir da identidade (espelho do `tema()` do servidor), pra prévia no painel. PURA. */
function _hsl([r, g, b]) {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b); const mn = Math.min(r, g, b); const l = (mx + mn) / 2; const d = mx - mn;
  if (!d) return [0, 0, l];
  const s = d / (1 - Math.abs(2 * l - 1));
  let h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h *= 60; if (h < 0) h += 360;
  return [h, s, l];
}
function _deHsl(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s; const x = c * (1 - Math.abs(((h / 60) % 2) - 1)); const m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return paraHex([r, g, b].map((v) => (v + m) * 255));
}
/** Tom escuro da MESMA cor com texto branco legível; branco/cinza → grafite. Espelho do servidor. PURA. */
export function tomEscuro(hex, alvo = 4.5) {
  const [h, s, l] = _hsl(rgb(hex));
  if (s < 0.12) return '#2f3a4a';
  const sat = Math.max(s, 0.45);
  for (let lum = Math.min(l, 0.6); lum >= 0.08; lum -= 0.01) {
    const c = _deHsl(h, sat, lum);
    if (contraste(c, '#ffffff') >= alvo) return c;
  }
  return ajustar(hex, '#ffffff', alvo, '#000000');
}
/**
 * Combinação automática (espelho de whiteLabelService.combinarCores): principal clara →
 * troca com a de destaque se ela for escura; senão tom escuro da própria e a clara vira destaque.
 */
export function combinarCores(corPrimaria, corDestaque) {
  const hex = (v) => (/^#[0-9a-f]{6}$/i.test(String(v || '').trim()) ? String(v).trim().toLowerCase() : null);
  const P = hex(corPrimaria) || '#283860';
  const A = hex(corDestaque);
  if (contraste(P, '#ffffff') >= 4.5) return { primaria: P, destaque: A || misturar(P, '#ffffff', 0.45), ajuste: 'nenhum' };
  if (A && contraste(A, '#ffffff') >= 4.5) return { primaria: A, destaque: P, ajuste: 'trocada' };
  const neutra = _hsl(rgb(P))[1] < 0.12;
  return { primaria: tomEscuro(P), destaque: A || (neutra ? '#5b6b88' : P), ajuste: 'escurecida' };
}

export function temaDaIdentidade(ident = {}) {
  const c = combinarCores(ident.cor_primaria, ident.cor_destaque);
  return { primaria: c.primaria, destaque: c.destaque, ajuste: c.ajuste, sobre_primaria: '#ffffff' };
}

/**
 * Cores que mais aparecem numa imagem (logo), sem branco/preto/cinza de fundo.
 * Devolve até `max` hex, da mais presente pra menos. Roda no navegador (canvas).
 */
export function coresDaImagem(url, max = 4) {
  return new Promise((ok) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const lado = 96;
        const esc = Math.min(1, lado / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * esc)); const h = Math.max(1, Math.round(img.height * esc));
        const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
        const cx = cv.getContext('2d'); cx.drawImage(img, 0, 0, w, h);
        const px = cx.getImageData(0, 0, w, h).data;
        const baldes = new Map();
        for (let i = 0; i < px.length; i += 4) {
          const [r, g, b, a] = [px[i], px[i + 1], px[i + 2], px[i + 3]];
          if (a < 200) continue;
          const mx = Math.max(r, g, b); const mn = Math.min(r, g, b);
          if (mx - mn < 28) continue;                       // cinza, branco, preto: fundo, não marca
          const k = [r, g, b].map((v) => Math.round(v / 24) * 24).join(',');
          const e = baldes.get(k) || { n: 0, r: 0, g: 0, b: 0 };
          e.n += 1; e.r += r; e.g += g; e.b += b; baldes.set(k, e);
        }
        const lista = [...baldes.values()].sort((x, y) => y.n - x.n)
          .map((e) => paraHex([e.r / e.n, e.g / e.n, e.b / e.n]));
        const unicas = [];
        for (const c of lista) { if (!unicas.some((u) => contraste(u, c) < 1.25)) unicas.push(c); if (unicas.length >= max) break; }
        ok(unicas);
      } catch (_) { ok([]); }
    };
    img.onerror = () => ok([]);
    img.src = url;
  });
}
